import type { ProductKind } from '@/product-types/contract';
import {
    PrintHeight,
    Product,
    Product2dCanvasRefApi,
    ProductApi,
    ProductMesh,
    ProductMeshApi,
    ObjectSettings,
    ObjectSettingsApi,
    TemplateData,
    UvMapData,
    UvMapCanvas
} from '@/types/Product';
import { Color, ColorApi, MaterialApi, HexColor } from '@/types/Material';
import { ObjectMapper } from './ObjectMapper';
import { MaterialDetailsMapper } from './MaterialDetailsMapper';
import { PrintProfileMapper } from './PrintProfileMapper';
import { convertToNumber } from '@/utils/index';
const { VITE_BACK_URL } = window.env;
const { t } = i18n.global;
export class ProductMapper {
    private static productProperties: Array<keyof ProductApi> = [
        'id',
        'name',
        'sku',
        'objPath',
        'textures',
        'impressions',
        'uvmapList',
        'measureList',
        'patronList',
        'ficheTechnique',
        'labelList',
        'uvmapType'
    ];

    static getProductFromData(productData: ProductApi, chosenColor?: string): Product {
        ProductMapper.checkProductData(productData);
        const {
            id,
            name,
            sku,
            objPath,
            awsobjpath,
            textures,
            impressions,
            uvmapList,
            measureList,
            patronList,
            ficheTechnique,
            labelList,
            uvmaps3DPath,
            configuration,
            uvmapType,
            objectSettings: objectSettingsApi,
            meshes: meshesApi,
            formatExport,
            printProfiles: printProfilesApi,
            canvases
        } = productData;

        const isCanvasFormat = (uvmapList?.length ?? 0) > 0 && 'zones' in uvmapList[0];
        const canvases2d = ProductMapper.mapCanvases2d(canvases);
        const kind = ProductMapper.resolveKind(productData, canvases2d);

        const product: Product = {
            id: id || '',
            name: name || '',
            reference: sku || '',
            modelPath: objPath || '',
            awsobjpath: awsobjpath || '',
            formatExport: formatExport ?? null,
            material: ProductMapper.getMaterial(textures?.[0], chosenColor),
            printHeightList: ProductMapper.mapPrintHeights(impressions),
            uvMapDataList: ProductMapper.mapUvMapDataList(uvmapList),
            uvMapCanvasList: isCanvasFormat ? ProductMapper.mapUvMapCanvasList(uvmapList as UvMapCanvas[]) : undefined,
            graduations: measureList ? measureList.flat() : [],
            templateDataList: ProductMapper.mapTemplateDataList(patronList),
            dataSheetPath: ficheTechnique || '',
            uvMapsPath: uvmaps3DPath || '',
            files: ProductMapper.generateFilesList(patronList?.[0], ficheTechnique),
            configuration: configuration || {},
            uvMapType: uvmapType,
            kind,
            canvases2d,
            objectSettings: ProductMapper.mapObjectSettings(objectSettingsApi),
            meshes: ProductMapper.mapMeshes(meshesApi),
            printProfiles: PrintProfileMapper.getPrintProfilesFromData(printProfilesApi),
            vdpEnabled: (productData as { vdpEnabled?: boolean }).vdpEnabled ?? false,
            // Mise en scène : le produit sait-il produire des mockups ? Le
            // document lui-même reste côté serveur, seul ce drapeau descend ici.
            mockupEnabled: (productData as { mockupEnabled?: boolean }).mockupEnabled ?? false
        };

        const translatedName: string | undefined = ObjectMapper.getTranslatedName(labelList);
        product.name = translatedName || product.name;
        return product;
    }

    /**
     * Type de support du produit.
     *
     * `typeProduct` (App\Enum\ProductType) est l'autorité. Il est nullable en
     * base et absent des produits antérieurs au champ : on retombe alors sur la
     * structure, qui est sans ambiguïté — un produit 2D n'a ni modèle 3D ni
     * atlas UV, mais au moins une face.
     */
    private static resolveKind(productData: ProductApi, canvases2d: Product2dCanvasRefApi[]): ProductKind {
        const declared = typeof productData.typeProduct === 'string' ? productData.typeProduct.toUpperCase() : '';
        if (declared === '2D') {
            return '2d';
        }
        if (declared === '3D') {
            return '3d';
        }
        const hasModel = Boolean(productData.objPath);
        const hasUvMaps = (productData.uvmapList?.length ?? 0) > 0;
        if (!hasModel && !hasUvMaps && canvases2d.length > 0) {
            return '2d';
        }
        return '3d';
    }

    private static mapCanvases2d(canvases?: Product2dCanvasRefApi[] | null): Product2dCanvasRefApi[] {
        if (!Array.isArray(canvases)) {
            return [];
        }
        return canvases
            .filter((canvas) => canvas && typeof canvas === 'object' && Boolean(canvas.id))
            .map((canvas) => ({
                id: String(canvas.id),
                name: canvas.name ?? '',
                path: canvas.path ?? '',
                presignedUrl: canvas.presignedUrl ?? null
            }));
    }

    private static checkProductData(productData: ProductApi): void {
        ProductMapper.productProperties.forEach((property) => {
            if (productData[property] == null) {
                console.error(`Property ${property} is missing or undefined in this product: ${productData.id}.`);
            }
        });
    }

    private static getMaterial(texture: MaterialApi | undefined, chosenColor?: string) {
        if (!texture) {
            return {
                name: '',
                materialDetails: [],
                colorList: [],
                colorsFor3DObject: [this.convertColorToHexa(undefined)]
            };
        }

        const { name, textures, colorList } = texture;
        const colorsFor3DObject = this.convertColors(texture, chosenColor);
        return {
            name: name || '',
            materialDetails: MaterialDetailsMapper.getMaterialDetailsFromData(textures || []) || [],
            colorList: this.mapColorList(colorList) || [],
            colorsFor3DObject: colorsFor3DObject?.length ? colorsFor3DObject : [this.convertColorToHexa(undefined)]
        };
    }

    private static mapPrintHeights(printHeightList: PrintHeight[]) {
        return (printHeightList || []).map((printHeight) => {
            const { sku, hauteur } = printHeight;

            return {
                sku: sku || '',
                hauteur: hauteur || ''
            };
        });
    }

    private static mapUvMapDataList(uvMapDataList: UvMapData[] | UvMapCanvas[]) {
        // Si c'est le nouveau format avec zones, utiliser les dimensions du canvas principal (pas des zones)
        if (uvMapDataList.length > 0 && 'zones' in uvMapDataList[0]) {
            return (uvMapDataList as UvMapCanvas[]).map((canvas) => {
                // Utiliser les dimensions du canvas principal
                return {
                    top: canvas.top ? convertToNumber(canvas.top) : 0,
                    left: canvas.left ? convertToNumber(canvas.left) : 0,
                    width: canvas.width ? convertToNumber(canvas.width) : 1024,
                    height: canvas.height ? convertToNumber(canvas.height) : 1024
                };
            });
        }
        // Ancien format
        return (uvMapDataList as UvMapData[]).map((uvMap) => {
            const { top, left, width, height } = uvMap;

            return {
                top: top ? convertToNumber(top) : 0,
                left: left ? convertToNumber(left) : 0,
                width: width ? convertToNumber(width) : 0,
                height: height ? convertToNumber(height) : 0
            };
        });
    }

    private static mapUvMapCanvasList(uvMapCanvasList: UvMapCanvas[]): UvMapCanvas[] {
        return (uvMapCanvasList || []).map((canvas) => ({
            meshId: canvas.meshId || 0,
            width: canvas.width ? convertToNumber(canvas.width) : 1024,
            height: canvas.height ? convertToNumber(canvas.height) : 1024,
            top: canvas.top ? convertToNumber(canvas.top) : 0,
            left: canvas.left ? convertToNumber(canvas.left) : 0,
            zones: (canvas.zones || []).map((zone) => ({
                meshId: zone.meshId || canvas.meshId || 0,
                label: zone.label || '',
                top: zone.top ? convertToNumber(zone.top) : 0,
                left: zone.left ? convertToNumber(zone.left) : 0,
                width: zone.width ? convertToNumber(zone.width) : 0,
                height: zone.height ? convertToNumber(zone.height) : 0,
                scaleX: zone.scaleX ? convertToNumber(zone.scaleX) : 1,
                scaleY: zone.scaleY ? convertToNumber(zone.scaleY) : 1,
                angle: zone.angle ? convertToNumber(zone.angle) : 0,
                id: zone.id || '',
                cameraPosition: zone.cameraPosition || { x: 0, y: 0, z: 0 },
                overflowDirection: zone.overflowDirection || null
            })),
            projectionMode: canvas.projectionMode,
            projectionParams: canvas.projectionParams,
            uvmapPath: canvas.uvmapPath || undefined
        }));
    }

    private static mapTemplateDataList(templateDataList: TemplateData[]) {
        return (templateDataList || []).map((template) => {
            const { width, height, proofWidth, proofHeight, pdf, psd, ai } = template;

            return {
                width: width ? convertToNumber(width) : 0,
                height: height ? convertToNumber(height) : 0,
                proofWidth: proofWidth ? convertToNumber(proofWidth) : 0,
                proofHeight: proofHeight ? convertToNumber(proofHeight) : 0,
                pdf: pdf || null,
                psd: psd || null,
                ai: ai || null
            };
        });
    }

    private static mapObjectSettings(api?: ObjectSettingsApi | null): ObjectSettings {
        if (!api) {
            return {
                autoAlign: false,
                autoCenter: false,
                autoScale: false,
                base: 'grid',
                rotationX: 0,
                rotationY: 0,
                rotationZ: 0,
                scale: 1
            };
        }
        return {
            autoAlign: api.autoAlign ?? false,
            autoCenter: api.autoCenter ?? api.autocenter ?? false,
            autoScale: api.autoScale ?? false,
            base: api.base ?? 'grid',
            rotationX: api.rotationX != null ? convertToNumber(api.rotationX) : 0,
            rotationY: api.rotationY != null ? convertToNumber(api.rotationY) : 0,
            rotationZ: api.rotationZ != null ? convertToNumber(api.rotationZ) : 0,
            scale: api.scale != null ? convertToNumber(api.scale) : 1
        };
    }

    private static mapMeshes(api?: ProductMeshApi[] | null): ProductMesh[] {
        if (!api || !Array.isArray(api)) {
            return [];
        }
        return api.map((mesh) => ({
            label: mesh.label ?? '',
            name: mesh.name ?? '',
            materials: (mesh.materials ?? [{ color: '#FFFFFF', materialId: 'default' }]).map((mat) => ({
                colorName: mat.colorName ?? '',
                color: mat.color ?? '#FFFFFF',
                materialId: mat.materialId ?? 'default',
                value: mat.value ?? null
            }))
        }));
    }

    private static mapColorList(colorList: ColorApi[]): Color[] {
        return (colorList || []).map((mainColor) => {
            const { sku, name, color, emissive, preview } = mainColor;

            return {
                name: name || sku,
                color: color || '',
                emissive: emissive || '',
                preview: preview || ''
            };
        });
    }

    private static convertColors(material: MaterialApi | undefined, chosenColorName: string | undefined): HexColor[] {
        const colorList = material?.colorList ?? [];
        const mainColorApi = colorList.find((c) => c?.name === chosenColorName) ?? colorList[0];

        const colors: HexColor[] = [
            this.convertColorToHexa(
                mainColorApi
                    ? {
                          color: mainColorApi.color,
                          emissive: mainColorApi.emissive
                      }
                    : undefined
            )
        ];

        const otherColors = material?.otherColors ?? [];
        const otherEmissives = material?.otherEmissivesColors ?? [];

        otherColors.forEach((c: any, index: number) => {
            const colorHex =
                (typeof c === 'string' ? c : c?.hex) ??
                (typeof c?.color === 'string' ? c.color : undefined) ??
                '#FFFFFF';

            const emissiveHex = otherEmissives?.[index]?.hex ?? '#000000';

            colors.push(
                this.convertColorToHexa({
                    color: colorHex,
                    emissive: emissiveHex
                })
            );
        });

        return colors;
    }

    public static convertColorToHexa(color: Partial<Color> | null | undefined): HexColor {
        const colorStr = typeof color?.color === 'string' && color.color.trim() ? color.color : '#FFFFFF';
        const emissiveStr = typeof color?.emissive === 'string' && color.emissive.trim() ? color.emissive : '#FFFFFF';

        const parsedColor = parseInt(colorStr.replace('#', ''), 16);
        const parsedEmissive = parseInt(emissiveStr.replace('#', ''), 16);

        const hexColor: HexColor = {
            color: Number.isFinite(parsedColor) ? parsedColor : 0xffffff,
            emissive: Number.isFinite(parsedEmissive) ? parsedEmissive : 0xffffff
        };

        return hexColor;
    }

    private static generateFilesList(files: TemplateData | undefined, ficheTechnique: string): ProductFile[] {
        return [
            ...Object.entries(files ?? {})
                .filter(([key]) => !['width', 'height'].includes(key))
                .filter(([_, path]) => path)
                .map(([extension, path]) => ({
                    name: `${t('Gabarit')} (.${extension})`,
                    link: `${VITE_BACK_URL}/downloadFile?filename=${path}`,
                    width: files.width,
                    height: files.height
                })),
            ...(ficheTechnique
                ? [{ name: t('FicheTechnique'), link: `${VITE_BACK_URL}/downloadFile?filename=${ficheTechnique}` }]
                : [])
        ];
    }
}
